import { useState, useEffect } from 'react';
import {
  X,
  CheckCircle2,
  XCircle,
  Info,
  Image as ImageIcon,
  Loader2,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

interface HighlightArea {
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DocumentTemplate {
  id: string;
  documentTypeId: string;
  exampleImageUrl: string;
  exampleImageAlt: string;
  highlightAreas: HighlightArea[] | null;
  doList: string[];
  dontList: string[];
  expectedFields: string[];
  isActive: boolean;
  documentType?: {
    code: string;
    name: string;
    description: string;
    kycType: string;
    entityType: string;
  };
}

interface DocumentTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentTypeCode: string;
  documentTypeName?: string;
}

export function DocumentTemplateModal({
  isOpen,
  onClose,
  documentTypeCode,
  documentTypeName,
}: DocumentTemplateModalProps) {
  const [template, setTemplate] = useState<DocumentTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [showHighlights, setShowHighlights] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Buscar template quando modal abre
  useEffect(() => {
    if (isOpen && documentTypeCode) {
      fetchTemplate();
    }
  }, [isOpen, documentTypeCode]);

  const fetchTemplate = async () => {
    setIsLoading(true);
    setError(null);
    setImageError(false);
    
    try {
      const response = await api.get(`/kyc/document-templates/${documentTypeCode}`);
      setTemplate(response.data);
    } catch (err: any) {
      console.error('Error fetching template:', err);
      setError(err.response?.data?.message || 'Não foi possível carregar o exemplo do documento');
    } finally {
      setIsLoading(false);
    }
  };

  // Fechar com ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const displayName = documentTypeName || template?.documentType?.name || documentTypeCode;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] mx-4 bg-surface-900 rounded-2xl shadow-2xl border border-surface-700 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-700 bg-surface-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Info className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-surface-100">
                Como enviar: {displayName}
              </h2>
              <p className="text-sm text-surface-400">
                Veja o exemplo e as dicas para enviar corretamente
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-700 transition-colors"
          >
            <X className="w-5 h-5 text-surface-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 animate-spin text-primary-500 mb-4" />
              <p className="text-surface-400">Carregando exemplo...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
              <p className="text-surface-300 text-center mb-4">{error}</p>
              <button
                onClick={fetchTemplate}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          ) : template ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Coluna da Imagem */}
              <div className="space-y-4">
                {/* Imagem de exemplo */}
                <div className="relative bg-surface-800 rounded-xl overflow-hidden border border-surface-700">
                  {/* Controles de zoom */}
                  <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-surface-900/80 rounded-lg p-1">
                    <button
                      onClick={() => setImageZoom(Math.max(0.5, imageZoom - 0.25))}
                      className="p-1.5 hover:bg-surface-700 rounded transition-colors"
                      title="Diminuir zoom"
                    >
                      <ZoomOut className="w-4 h-4 text-surface-300" />
                    </button>
                    <span className="text-xs text-surface-400 px-2">{Math.round(imageZoom * 100)}%</span>
                    <button
                      onClick={() => setImageZoom(Math.min(2, imageZoom + 0.25))}
                      className="p-1.5 hover:bg-surface-700 rounded transition-colors"
                      title="Aumentar zoom"
                    >
                      <ZoomIn className="w-4 h-4 text-surface-300" />
                    </button>
                  </div>

                  {/* Toggle highlights */}
                  {template.highlightAreas && template.highlightAreas.length > 0 && (
                    <div className="absolute top-2 left-2 z-10">
                      <button
                        onClick={() => setShowHighlights(!showHighlights)}
                        className={cn(
                          "px-3 py-1.5 text-xs rounded-lg transition-colors",
                          showHighlights
                            ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                            : "bg-surface-900/80 text-surface-400"
                        )}
                      >
                        {showHighlights ? 'Ocultar áreas' : 'Mostrar áreas'}
                      </button>
                    </div>
                  )}

                  {/* Container da imagem */}
                  <div className="relative overflow-auto max-h-[400px] p-4">
                    {imageError ? (
                      <div className="flex flex-col items-center justify-center py-12 text-surface-400">
                        <ImageIcon className="w-16 h-16 mb-4 opacity-50" />
                        <p className="text-sm">Imagem de exemplo não disponível</p>
                        <p className="text-xs mt-1">Siga as dicas ao lado para enviar corretamente</p>
                      </div>
                    ) : (
                      <div
                        className="relative inline-block transition-transform duration-200"
                        style={{ transform: `scale(${imageZoom})`, transformOrigin: 'top left' }}
                      >
                        <img
                          src={template.exampleImageUrl}
                          alt={template.exampleImageAlt}
                          className="max-w-full rounded-lg"
                          onError={() => setImageError(true)}
                        />
                        
                        {/* Áreas destacadas */}
                        {showHighlights && template.highlightAreas && template.highlightAreas.map((area, index) => (
                          <div
                            key={index}
                            className="absolute border-2 border-primary-500 bg-primary-500/10 rounded"
                            style={{
                              left: area.x,
                              top: area.y,
                              width: area.width,
                              height: area.height,
                            }}
                          >
                            <span className="absolute -top-6 left-0 text-xs bg-primary-500 text-white px-2 py-0.5 rounded whitespace-nowrap">
                              {area.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Descrição do documento */}
                {template.documentType?.description && (
                  <div className="p-4 bg-surface-800/50 rounded-xl border border-surface-700">
                    <p className="text-sm text-surface-300">{template.documentType.description}</p>
                  </div>
                )}
              </div>

              {/* Coluna das Dicas */}
              <div className="space-y-4">
                {/* O que fazer */}
                <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <h3 className="font-semibold text-green-400">O que fazer</h3>
                  </div>
                  <ul className="space-y-2">
                    {template.doList.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-surface-300">
                        <span className="text-green-400 mt-0.5">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* O que NÃO fazer */}
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <h3 className="font-semibold text-red-400">O que NÃO fazer</h3>
                  </div>
                  <ul className="space-y-2">
                    {template.dontList.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-surface-300">
                        <span className="text-red-400 mt-0.5">✗</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Campos esperados */}
                {template.expectedFields && template.expectedFields.length > 0 && (
                  <div className="bg-surface-800/50 border border-surface-700 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Info className="w-5 h-5 text-primary-400" />
                      <h3 className="font-semibold text-surface-200">Informações que serão verificadas</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {template.expectedFields.map((field, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-surface-700 text-surface-300 rounded-lg"
                        >
                          {formatFieldName(field)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-surface-700 bg-surface-800/50">
          <div className="flex items-center justify-between">
            <p className="text-xs text-surface-500">
              Dica: Siga as orientações acima para evitar que seu documento seja rejeitado
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              Entendi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper para formatar nomes de campos
function formatFieldName(field: string): string {
  const fieldNames: Record<string, string> = {
    name: 'Nome',
    cpf: 'CPF',
    cnpj: 'CNPJ',
    birthDate: 'Data de Nascimento',
    documentNumber: 'Número do Documento',
    expirationDate: 'Data de Validade',
    holderName: 'Nome do Titular',
    street: 'Rua',
    number: 'Número',
    neighborhood: 'Bairro',
    city: 'Cidade',
    state: 'Estado',
    zipCode: 'CEP',
    issueDate: 'Data de Emissão',
    faceMatch: 'Verificação Facial',
    documentVisible: 'Documento Visível',
    livenessCheck: 'Prova de Vida',
    grossIncome: 'Renda Bruta',
    netIncome: 'Renda Líquida',
    referenceMonth: 'Mês de Referência',
    employerName: 'Nome do Empregador',
    companyName: 'Razão Social',
    tradeName: 'Nome Fantasia',
    partners: 'Sócios',
    registrationNumber: 'Número de Registro',
    registrationDate: 'Data de Registro',
    status: 'Situação Cadastral',
    mainActivity: 'Atividade Principal',
    fiscalYear: 'Ano Fiscal',
    totalAssets: 'Ativo Total',
    totalLiabilities: 'Passivo Total',
    equity: 'Patrimônio Líquido',
    bankCode: 'Código do Banco',
    bankName: 'Nome do Banco',
    agency: 'Agência',
    accountNumber: 'Número da Conta',
    holderDocument: 'Documento do Titular',
    pixKey: 'Chave Pix',
    keyType: 'Tipo da Chave',
  };

  return fieldNames[field] || field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
}

export default DocumentTemplateModal;
